import { CircleCheckBig, Plus, Search, SquarePen, Trash } from 'lucide-react';
import { useMemo, useState } from 'react';
import { AppShell } from '@/components/layout/AppShell';
import { ActionGateBanner } from '@/components/patterns/ActionGateBanner';
import { useEmailVerified } from '@/hooks/useEmailVerified';
import { toast } from '@/lib/notify';
import { cn } from '@/lib/utils';

import { useDeleteRole } from '../api/useDeleteRole';
import { useListPermissions } from '../api/useListPermissions';
import { useListRoles } from '../api/useListRoles';
import { RoleFormModal } from '../components/RoleFormModal';
import type { RoleDto } from '../schemas/roleSchemas';

// Splits a role's permission codes into columns of at most 4 for the table
// multi-column chip grid (matches the designer's HTML layout).
function chunkPermissions(codes: string[], colSize = 4): string[][] {
  const cols: string[][] = [];
  for (let i = 0; i < codes.length; i += colSize) {
    cols.push(codes.slice(i, i + colSize));
  }
  return cols;
}

// Formats a permission code (e.g. "Workflow.Create") into a readable label.
function permLabel(code: string) {
  const parts = code.split('.');
  if (parts.length >= 2) {
    return `${parts[parts.length - 1]} ${parts[0]}`;
  }
  return code;
}

export function RolesScreen() {
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<RoleDto | null>(null);

  const isEmailVerified = useEmailVerified();

  const { data: roles = [], isLoading } = useListRoles();
  const { data: permissions = [] } = useListPermissions();
  const deleteRole = useDeleteRole();

  const filtered = useMemo(() => {
    if (!search.trim()) return roles;
    const q = search.toLowerCase();
    return roles.filter(
      (r) => r.name.toLowerCase().includes(q) || r.description?.toLowerCase().includes(q),
    );
  }, [roles, search]);

  function openCreate() {
    setEditingRole(null);
    setModalOpen(true);
  }

  function openEdit(role: RoleDto) {
    setEditingRole(role);
    setModalOpen(true);
  }

  function handleDelete(role: RoleDto) {
    if (role.isSystem) {
      toast.error('System roles cannot be deleted');
      return;
    }
    if (!confirm(`Delete role "${role.name}"? This cannot be undone.`)) return;
    deleteRole.mutate(role.id, {
      onSuccess: () => toast.success('Role deleted'),
      onError: () => toast.error('Failed to delete role'),
    });
  }

  return (
    <AppShell title="User Roles">
      <div className="flex flex-col gap-4 lg:gap-5 text-black-logo">
        {/* Header */}
        <div className="grid xl:grid-cols-6 gap-6 gap-y-2">
          <div className="space-y-1.5 xl:col-span-4">
            <h4 className="text-lg leading-5.5 font-medium">Roles & Permissions</h4>
            <p className="text-sm leading-[17px] text-gray66">
              Define roles and assign appropriate access levels to organization members to ensure
              secure and efficient management of responsibilities.
            </p>
          </div>
          <div className="flex min-[480px]:items-center justify-end max-[480px]:flex-col gap-2.5 xl:col-span-2">
            <div className="flex flex-col gap-1 xl:max-w-[330px] w-full">
              <div className="relative">
                <Search className="w-3.5 h-3.5 absolute top-1/2 start-4 -translate-y-1/2 text-gray66" />
                <input
                  type="search"
                  className="sc-input input-small ps-9 w-full"
                  placeholder="Search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>
            <button
              type="button"
              onClick={openCreate}
              disabled={!isEmailVerified}
              className="btn btn-primary btn-small whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus className="w-3 h-3" />
              <span>Create User Role</span>
            </button>
          </div>
        </div>

        {!isEmailVerified && <ActionGateBanner />}

        {/* Table */}
        <div className="table-wrapper overflow-x-auto overflow-y-hidden border border-placeholder rounded-lg custom-scrollbar">
          <table className="min-w-full border-collapse text-sm leading-[17px]">
            <thead className="bg-[#F4F6FD] text-black-logo border-b border-[#CBD5E1]">
              <tr className="text-start divide-x divide-[#CBD5E1]">
                <th className="px-3 py-[8.25px] font-medium whitespace-nowrap min-w-46">Roles</th>
                <th className="px-3 py-[8.25px] font-medium whitespace-nowrap">Permissions</th>
                <th className="px-3 py-[8.25px] font-medium whitespace-nowrap text-center sticky right-0 bg-[#F4F6FD] shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.06)]">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#CBD5E1] bg-white font-normal text-gray66">
              {isLoading ? (
                <tr>
                  <td colSpan={3} className="px-3 py-8 text-center text-gray66">
                    Loading…
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={3} className="px-3 py-8 text-center text-gray66">
                    {search ? 'No roles match your search.' : 'No roles yet.'}
                  </td>
                </tr>
              ) : (
                filtered.map((role) => {
                  const cols = chunkPermissions(role.permissionCodes);
                  return (
                    <tr
                      key={role.id}
                      className="hover:bg-placeholder/5 transition divide-x divide-[#CBD5E1]"
                    >
                      <td className="px-3 py-[11px] font-semibold text-lg text-black-logo align-top">
                        <div className="flex items-center gap-2">
                          <span>{role.name}</span>
                          {role.isSystem && (
                            <span className="text-[10px] font-medium text-primary bg-blue-tint px-1.5 py-0.5 rounded-full">
                              system
                            </span>
                          )}
                        </div>
                        {role.description && (
                          <p className="text-xs text-gray66 font-normal mt-0.5 max-w-[160px]">
                            {role.description}
                          </p>
                        )}
                      </td>
                      <td className="px-3 py-[11px]">
                        {cols.length === 0 ? (
                          <span className="text-gray66 text-xs">No permissions</span>
                        ) : (
                          <div className="flex items-start gap-3 sm:gap-6 lg:gap-12 lg:py-0.5">
                            {cols.map((col) => (
                              <ul key={col[0] ?? 'empty'} className="space-y-2">
                                {col.map((code) => (
                                  <li
                                    key={code}
                                    className="flex items-center gap-1.5 px-1 py-px text-gray30 whitespace-nowrap"
                                  >
                                    <CircleCheckBig className="w-3.5 h-3.5 shrink-0" />
                                    <span className="leading-[150%]">{permLabel(code)}</span>
                                  </li>
                                ))}
                              </ul>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-[11px] text-center align-top sticky right-0 bg-white shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.06)]">
                        <ul className="flex items-center justify-center gap-[21px]">
                          <li>
                            <button
                              type="button"
                              onClick={() => openEdit(role)}
                              disabled={!isEmailVerified}
                              className={cn(
                                'flex items-center gap-3 py-1.5 px-1 hover:text-green00',
                                (role.isSystem || !isEmailVerified) &&
                                  'opacity-50 cursor-not-allowed',
                              )}
                              title={
                                !isEmailVerified
                                  ? 'Verify your email to edit roles'
                                  : role.isSystem
                                    ? 'System roles cannot be edited'
                                    : 'Edit role'
                              }
                            >
                              <SquarePen className="w-5 h-5" />
                            </button>
                          </li>
                          <li>
                            <button
                              type="button"
                              onClick={() => handleDelete(role)}
                              disabled={deleteRole.isPending || !isEmailVerified}
                              className={cn(
                                'flex items-center gap-3 py-1.5 px-1 hover:text-red-600',
                                (role.isSystem || !isEmailVerified) &&
                                  'opacity-50 cursor-not-allowed',
                              )}
                              title={
                                !isEmailVerified
                                  ? 'Verify your email to delete roles'
                                  : role.isSystem
                                    ? 'System roles cannot be deleted'
                                    : 'Delete role'
                              }
                            >
                              <Trash className="w-5 h-5" />
                            </button>
                          </li>
                        </ul>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <RoleFormModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        role={editingRole}
        permissions={permissions}
      />
    </AppShell>
  );
}
